import React from 'react';
import { motion } from 'framer-motion';
import { LuCheck } from 'react-icons/lu';
import FaceMeshOverlay from '../oracle/FaceMeshOverlay';
import PortraitScoreBar from '../oracle/PortraitScoreBar';

// The Create surface's scan capture viewport (design-recreation CREATE).
// 440x587 framed plinth on desktop, full-width 360h on mobile. The viewport
// IS the dropzone while idle — no mock face backdrop; the surface stays
// empty until the user supplies a photo.
//
//   idle ──drop/pick──▶ preparing ──▶ staged ──CAPTURE──▶ analyzing
//                                                            │
//                done ◀── success            rejected/error ◀┘
//
// Props:
//   phase        'idle'|'preparing'|'staged'|'quotaBlocked'|'analyzing'|'done'|'rejected'|'error'|'clientError'
//                (quotaBlocked renders like staged — photo held, status pill
//                carries the unlock prompt)
//   previewUrl   object URL of the staged portrait (null while idle)
//   landmarks    facePrep landmarks (optional, analyzing mesh)
//   meshEdges    facePrep mesh edges (optional)
//   score10      formatted "/10" score string (done state)
//   statusText   status pill copy
//   statusColor  status pill hex color
//   rootProps    dropzone getRootProps() result
//   inputProps   dropzone getInputProps() result
//   isDragActive dropzone drag state
//   fill         fill the parent edge-to-edge (sharp corners, no border) and
//                skip the dropzone input — the mobile capture window, where the
//                input lives in the picker sheet instead
const Bracket = ({ className }) => (
  <div
    aria-hidden="true"
    className={[
      'absolute w-[30px] h-[30px] lg:w-10 lg:h-10 border-[rgba(201,164,92,0.7)] pointer-events-none',
      className,
    ].join(' ')}
  />
);

const ScanViewport = ({
  phase,
  previewUrl,
  landmarks,
  meshEdges,
  score10,
  percentile,
  statusText,
  statusColor,
  rootProps,
  inputProps,
  isDragActive,
  fill = false,
}) => {
  const showPhoto = Boolean(previewUrl);
  const analyzing = phase === 'analyzing';
  const done = phase === 'done';
  return (
    <div
      {...(fill ? {} : rootProps)}
      aria-label="scan capture viewport"
      className={[
        'relative overflow-hidden bg-[#0f0f0e] shadow-plinth',
        fill
          ? 'w-full h-full rounded-none border-0'
          : [
              'w-full flex-1 min-h-[200px] lg:flex-none lg:w-[440px] lg:h-[587px]',
              'rounded-[18px] lg:rounded-card border',
              isDragActive ? 'border-aurum' : 'border-[rgba(255,255,255,0.10)]',
            ].join(' '),
        phase === 'idle' ? 'cursor-pointer' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* In fill mode (mobile capture window) the dropzone input lives in the
          intro/picker, not here — skip rendering a second wired input. */}
      {!fill && <input {...inputProps} aria-label="portrait upload" />}

      {showPhoto && (
        <img
          src={previewUrl}
          alt="staged portrait"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* inner vignette */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ boxShadow: 'inset 0 0 90px 30px rgba(0,0,0,0.7)' }}
      />

      {analyzing && (
        <div className="absolute inset-0 bg-obsidian/40" data-testid="analyzing-scrim" aria-hidden="true" />
      )}
      {analyzing && landmarks && (
        <FaceMeshOverlay
          className="absolute inset-0 w-full h-full"
          landmarks={landmarks}
          edges={meshEdges}
          density={80}
          pulse
          scanline
        />
      )}

      {/* alignment brackets */}
      <Bracket className="left-[18px] top-[18px] lg:left-[30px] lg:top-[30px] border-l-2 border-t-2" />
      <Bracket className="right-[18px] top-[18px] lg:right-[30px] lg:top-[30px] border-r-2 border-t-2" />
      <Bracket className="left-[18px] bottom-[18px] lg:left-[30px] lg:bottom-[30px] border-l-2 border-b-2" />
      <Bracket className="right-[18px] bottom-[18px] lg:right-[30px] lg:bottom-[30px] border-r-2 border-b-2" />

      {/* idle affordance — no mock face, just the click/drop prompt. Gated to
          the idle phase so it doesn't flash during preparing (no photo yet). */}
      {phase === 'idle' && !showPhoto && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-bone-dim">
          <span className="font-display uppercase tracking-[0.18em] text-[11px] opacity-80">
            {isDragActive ? 'RELEASE TO STAGE' : 'TAP OR DROP A PORTRAIT'}
          </span>
        </div>
      )}

      {/* scan-sweep line (design keyframe: top 5% -> 93%, 2.3s) */}
      {analyzing && (
        <motion.div
          aria-hidden="true"
          className="absolute left-[8%] right-[8%] h-[2px] pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, rgba(230,192,131,0), rgba(242,214,166,0.95), rgba(230,192,131,0))',
            boxShadow: '0 0 18px rgba(230,192,131,0.9)',
          }}
          initial={{ top: '5%' }}
          animate={{ top: ['5%', '93%'] }}
          transition={{ duration: 2.3, ease: 'easeInOut', repeat: Infinity }}
        />
      )}

      {done && (
        <>
          {/* gold ring check — lifted above the eye-line bar */}
          <div className="absolute left-1/2 top-[10%] -translate-x-1/2 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.32, ease: [0.65, 0, 0.35, 1] }}
              className="w-16 h-16 lg:w-[76px] lg:h-[76px] rounded-full bg-[rgba(0,0,0,0.55)] border-[1.5px] border-aurum flex items-center justify-center"
              style={{ boxShadow: '0 0 24px rgba(201,164,92,0.5)' }}
              data-testid="scan-done-check"
            >
              <LuCheck size={32} color="#E6C083" strokeWidth={1.8} aria-hidden="true" />
            </motion.div>
          </div>
          <PortraitScoreBar score={score10} size="xl" lifted={false} />
          {percentile && (
            <div
              className="absolute left-1/2 -translate-x-1/2 top-[calc(40%+34px)] lg:top-[calc(40%+42px)] flex flex-col items-center gap-1 pointer-events-none"
              data-testid="scan-percentile"
            >
              <span className="font-censor font-bold uppercase tracking-normal bg-obsidian text-bone-pure text-sm px-2 py-1 rounded-none">
                {percentile}
              </span>
              <span className="font-display uppercase text-[9px] lg:text-[10px] tracking-[0.24em] text-aurum">
                GLOBAL PERCENTILE
              </span>
            </div>
          )}
        </>
      )}

      {/* status pill */}
      <div className="absolute left-1/2 bottom-[18px] lg:bottom-6 -translate-x-1/2 whitespace-nowrap bg-[rgba(0,0,0,0.7)] border border-[rgba(255,255,255,0.12)] rounded-full px-4 py-[9px] lg:px-[22px] lg:py-[11px] flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="w-[7px] h-[7px] rounded-full motion-safe:animate-censor-pulse"
          style={{ background: statusColor }}
        />
        <span
          className="font-display uppercase text-[10px] lg:text-[11.5px] tracking-[0.2em]"
          style={{ color: statusColor }}
        >
          {statusText}
        </span>
      </div>
    </div>
  );
};

export default ScanViewport;
